import { useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const COCKTAILS = [
  { img: "/cocktails/kora-kiss.jpg", name: "Kora Kiss", desc: "Tequila, Aperol, pepper-infused blood orange & lime." },
  { img: "/cocktails/watermelon-margarita.jpg", name: "Watermelon Margarita", desc: "Tequila, agua de sandía & blood orange simple syrup." },
  { img: "/cocktails/limonada-cremosa.jpg", name: "Limonada Cremosa", desc: "Vodka, limoncello, sour mix, lime, simple syrup & sweet cream." },
  { img: "/cocktails/limoncello-martini.jpg", name: "Limoncello Martini", desc: "Vodka, limoncello, lemon & simple syrup." },
  { img: "/cocktails/pineapple-mojito.jpg", name: "Pineapple Mojito", desc: "Rum, pineapple, St-Germain, sour mix, mint & a splash of soda." },
  { img: "/cocktails/pink-lady.jpg", name: "Pink Lady", desc: "Lemon vodka, peach schnapps, cranberry & lemonade." },
  { img: "/cocktails/sweet-tooth.jpg", name: "Sweet Tooth", desc: "Vanilla vodka, watermelon pucker, cranberry, sour mix & vanilla simple syrup." },
  { img: "/cocktails/fly-by.jpg", name: "Fly By", desc: "Bourbon, amaretto, Aperol, lemon & passion fruit." },
  { img: "/cocktails/full-of-flowers.jpg", name: "Full of Flowers", desc: "Vodka, elderflower, hibiscus simple syrup, grapefruit, sour mix & pomegranate juice." },
  { img: "/cocktails/texas-tea-lem.jpg", name: "Texas Tea/Lem", desc: "Vodka, gin, rum, peach & sour mix, finished with lemonade or Coke." },
  { img: "/cocktails/honey-apple.jpg", name: "Honey Apple", desc: "Bourbon, apple cider, butterscotch, honey, lemon & walnut bitters." },
  { img: "/cocktails/pumpkin-king.jpg", name: "Pumpkin King", desc: "Whiskey, pumpkin butter, honey & egg white, with honeycomb." },
  { img: "/cocktails/nyn.jpg", name: "N.Y.N.", desc: "Tequila, orange juice & passion fruit, topped with blue curaçao foam." },
];

export default function SignatureCocktails() {
  const trackRef = useRef(null);

  const scroll = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.7, behavior: "smooth" });
  };

  return (
    <section id="cocktails" data-testid="cocktails-section" className="grain bg-espresso-deep px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <Reveal>
              <span className="eyebrow text-gold">Signature cocktails</span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display text-4xl text-cream sm:text-5xl lg:text-6xl">
                Our signature <em className="italic text-rose">pours</em>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <div className="flex items-center gap-3">
              <p className="max-w-xs text-sm leading-relaxed text-cream/60 sm:text-base">
                Crafted for your event, garnished like jewellery. Included with The Full Pour —
                or add two more to any package.
              </p>
              <button
                data-testid="cocktail-prev"
                onClick={() => scroll(-1)}
                aria-label="Previous cocktails"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-cream/25 text-cream transition-all duration-300 hover:border-gold hover:bg-gold hover:text-espresso"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                data-testid="cocktail-next"
                onClick={() => scroll(1)}
                aria-label="Next cocktails"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-cream/25 text-cream transition-all duration-300 hover:border-gold hover:bg-gold hover:text-espresso"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div
            ref={trackRef}
            data-testid="cocktail-carousel"
            className="mt-12 flex snap-x snap-mandatory gap-6 overflow-x-auto pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {COCKTAILS.map((c, i) => (
              <figure key={c.name} data-testid={`cocktail-card-${i}`} className="shrink-0 snap-start">
                <div className="w-fit">
                  <img
                    src={c.img}
                    alt={`${c.name} — Shaken Society signature cocktail`}
                    loading="lazy"
                    className="h-[175px] w-auto rounded-2xl shadow-[0_20px_40px_-24px_rgba(0,0,0,0.7)] transition-transform duration-700 ease-out hover:scale-[1.03] sm:h-[205px]"
                  />
                  <p
                    className="mt-2.5 w-0 min-w-full pr-1 text-[11px] leading-relaxed text-cream/55 sm:text-xs"
                    data-testid={`cocktail-desc-${i}`}
                  >
                    {c.desc}
                  </p>
                </div>
              </figure>
            ))}
          </div>
        </Reveal>
        <p className="mt-2 text-[11px] tracking-wide text-cream/40" data-testid="cocktail-allergy-note">
          Pumpkin King contains egg white — please let us know about any allergies.
        </p>
      </div>
    </section>
  );
}
